/**
 * QR Code Extractor Service
 * 
 * Extracts QR codes from documents (primarily boarding passes)
 * and stores them for quick access offline.
 */

import { functions, httpsCallable } from '@/lib/functions';
import { offlineStorage } from './offlineStorage';

const QR_CODE_CACHE_KEY = '@tripflow_qr_codes';

type QrCodeData = {
  documentId: string;
  qrCode: string;
  extractedAt: number;
  source: 'extracted' | 'manual';
  documentName?: string;
  documentType?: string;
};

type QrCodeCache = {
  [documentId: string]: QrCodeData;
};

/**
 * Extract QR code from a document (calls cloud function)
 */
export async function extractQrCodeFromDocument(
  documentId: string,
  storagePath: string
): Promise<{ qrCode: string | null; found: boolean; error?: string }> {
  try {
    const extractQrCode = httpsCallable<{ documentId: string; storagePath: string }, { qrCode?: string; found: boolean; error?: string }>(
      functions,
      'extractQrCodeFromDocument'
    );

    const result = await extractQrCode({
      documentId,
      storagePath,
    });

    const data = result.data;

    if (data.found && data.qrCode) {
      // Cache the QR code
      await cacheQrCode(documentId, data.qrCode, 'extracted');
      return { qrCode: data.qrCode, found: true };
    }

    return { qrCode: null, found: false, error: data.error || 'No QR code found' };
  } catch (error) {
    console.error('[QrCodeExtractor] Error extracting QR code:', error);
    return {
      qrCode: null,
      found: false,
      error: error instanceof Error ? error.message : 'Failed to extract QR code',
    };
  }
}

/**
 * Cache a QR code for a document
 */
export async function cacheQrCode(
  documentId: string,
  qrCode: string,
  source: 'extracted' | 'manual',
  documentName?: string,
  documentType?: string
): Promise<void> {
  try {
    const cached = await offlineStorage.get<QrCodeCache>(QR_CODE_CACHE_KEY) || {};
    
    cached[documentId] = {
      documentId,
      qrCode,
      extractedAt: Date.now(),
      source,
      documentName,
      documentType,
    };

    await offlineStorage.set(QR_CODE_CACHE_KEY, cached);
    console.log(`[QrCodeExtractor] Cached QR code for document ${documentId}`);
  } catch (error) {
    console.error(`[QrCodeExtractor] Error caching QR code for ${documentId}:`, error);
  }
}

/**
 * Get cached QR code for a document
 */
export async function getCachedQrCode(documentId: string): Promise<string | null> {
  try {
    const cached = await offlineStorage.get<QrCodeCache>(QR_CODE_CACHE_KEY);
    if (!cached || !cached[documentId]) {
      return null;
    }

    return cached[documentId].qrCode;
  } catch (error) {
    console.error(`[QrCodeExtractor] Error getting cached QR code for ${documentId}:`, error);
    return null;
  }
}

/**
 * Get all cached QR codes
 */
export async function getAllCachedQrCodes(): Promise<QrCodeData[]> {
  try {
    const cached = await offlineStorage.get<QrCodeCache>(QR_CODE_CACHE_KEY);
    if (!cached) {
      return [];
    }

    return Object.values(cached);
  } catch (error) {
    console.error('[QrCodeExtractor] Error getting all cached QR codes:', error);
    return [];
  }
}

/**
 * Remove cached QR code for a document
 */
export async function removeCachedQrCode(documentId: string): Promise<void> {
  try {
    const cached = await offlineStorage.get<QrCodeCache>(QR_CODE_CACHE_KEY);
    if (!cached || !cached[documentId]) {
      return;
    }

    delete cached[documentId];
    await offlineStorage.set(QR_CODE_CACHE_KEY, cached);
    console.log(`[QrCodeExtractor] Removed cached QR code for document ${documentId}`);
  } catch (error) {
    console.error(`[QrCodeExtractor] Error removing cached QR code for ${documentId}:`, error);
  }
}

/**
 * Clear all cached QR codes
 */
export async function clearAllQrCodes(): Promise<void> {
  try {
    await offlineStorage.remove(QR_CODE_CACHE_KEY);
    console.log('[QrCodeExtractor] Cleared all cached QR codes');
  } catch (error) {
    console.error('[QrCodeExtractor] Error clearing all QR codes:', error);
  }
}

